import { useState, useEffect } from "react";

/**
 * Whether there is a connection, and whether the app is installed.
 *
 * `navigator.onLine` only knows whether there is a network to speak to, not
 * whether anything answers on it — a hall with one bar reports online and can
 * still fail every request. It is right often enough to be worth saying, and
 * the app does not depend on it: every screen already falls back on its own.
 */
export default function useOffline() {
  const [online, setOnline] = useState(
    () => typeof navigator === "undefined" || navigator.onLine !== false
  );

  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener("online", up);
    window.addEventListener("offline", down);
    return () => {
      window.removeEventListener("online", up);
      window.removeEventListener("offline", down);
    };
  }, []);

  return online;
}

/**
 * The browser's own install prompt, held until there is somewhere sensible to
 * offer it. Chrome fires it once and only once, so it has to be caught the
 * moment it arrives rather than when a button happens to be rendered.
 */
/* Saying no once should mean no. Kept on the device rather than in a backup or
   an account, because it is a fact about this browser rather than about the
   person — installing on a phone says nothing about what a laptop should
   offer. */
export const INSTALL_DISMISSED_KEY = "ppt_install_dismissed_v1";

export function useInstallPrompt() {
  const [prompt, setPrompt] = useState(null);
  const [dismissed, setDismissed] = useState(() => {
    try {
      return window.localStorage.getItem(INSTALL_DISMISSED_KEY) === "1";
    } catch {
      return false;
    }
  });
  const [installed, setInstalled] = useState(
    () =>
      typeof window !== "undefined" &&
      (window.matchMedia?.("(display-mode: standalone)").matches ||
        window.navigator.standalone === true)
  );

  useEffect(() => {
    const held = (e) => {
      e.preventDefault();
      setPrompt(e);
    };
    const done = () => { setInstalled(true); setPrompt(null); };
    window.addEventListener("beforeinstallprompt", held);
    window.addEventListener("appinstalled", done);
    return () => {
      window.removeEventListener("beforeinstallprompt", held);
      window.removeEventListener("appinstalled", done);
    };
  }, []);

  const install = async () => {
    if (!prompt) return false;
    prompt.prompt();
    const { outcome } = await prompt.userChoice;
    // It cannot be used twice, whatever the answer was.
    setPrompt(null);
    return outcome === "accepted";
  };

  const dismiss = () => {
    try {
      window.localStorage.setItem(INSTALL_DISMISSED_KEY, "1");
    } catch {
      /* a blocked localStorage means it comes back next visit, not that the
         button does nothing now */
    }
    setDismissed(true);
  };

  return {
    canInstall: Boolean(prompt) && !installed && !dismissed,
    installed,
    install,
    dismiss,
  };
}
